import { Card } from "@/components/ui/card";
import { Users, Clock, AlertTriangle, UserCheck, Wallet, TrendingUp, TrendingDown } from "lucide-react";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useDashboardExtendedStats } from "@/hooks/useDashboardExtendedStats";
import { Skeleton } from "@/components/ui/skeleton";

const TrendBadge = ({ value }: { value: number }) => {
  const isPositive = value >= 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const bgClass = isPositive
    ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
    : "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400";
  const prefix = isPositive ? "+" : "";

  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-semibold ${bgClass}`}>
      <Icon className="w-3 h-3" />
      {prefix}{value}%
    </span>
  );
};

export const DashboardStats = () => {
  const { data: stats, isLoading } = useDashboardStats();
  const { data: extended, isLoading: extLoading } = useDashboardExtendedStats();

  const loading = isLoading || extLoading;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
      {/* Left: Wide discharge card - spans full height of right column */}
      <Card className="p-4 lg:row-span-2">
        {loading ? (
          <div className="flex flex-col sm:flex-row gap-4 h-full">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-9 w-20" />
              <div className="flex items-center gap-2 mt-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Skeleton className="h-7 w-16" />
            </div>
            <div className="flex-1 sm:border-l border-t sm:border-t-0 border-border pt-3 sm:pt-0 sm:pl-4 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-1 h-8 rounded-full" />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <Skeleton className="h-6 w-10" />
                </div>
              ))}
            </div>
          </div>
        ) : (
        <div className="flex flex-col sm:flex-row gap-4 h-full">
          {/* Left half: Discharge counts */}
          <div className="flex-1 flex flex-col justify-center space-y-3">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Total Discharges Completed Today</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {loading ? "..." : stats?.totalDischargesToday || 0}
              </span>
              {!loading && stats && (
                <TrendBadge value={stats.dischargeTrend} />
              )}
            </div>

            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs font-medium">Total Discharge Pending</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-foreground">
                {loading ? "..." : stats?.pendingDischargesToday || 0}
              </span>
              {!loading && stats && (
                <TrendBadge value={stats.pendingTrend} />
              )}
            </div>
          </div>

          {/* Right half: Breakdown */}
          <div className="flex-1 flex flex-col justify-center sm:border-l border-t sm:border-t-0 border-border pt-3 sm:pt-0 sm:pl-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-8 rounded-full bg-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Patients Marked</p>
                  <p className="text-xs text-muted-foreground">For Discharge Today</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-lg font-semibold text-foreground">
                  {loading ? "..." : extended?.patientsMarked || 0}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-8 rounded-full bg-success" />
                <div>
                  <p className="text-sm font-medium text-foreground">Planned Discharge</p>
                  <p className="text-xs text-muted-foreground">As per LOS</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-lg font-semibold text-foreground">
                  {loading ? "..." : extended?.plannedDischarge || 0}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-8 rounded-full bg-warning" />
                <div>
                  <p className="text-sm font-medium text-foreground">LOS Carry-Over</p>
                  <p className="text-xs text-muted-foreground">From Previous Day</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-lg font-semibold text-foreground">
                  {loading ? "..." : extended?.losCarryOver || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
        )}
      </Card>

      {/* Right column top row: Avg Discharge Time + Total Escalations */}
      <div className="grid grid-cols-2 gap-3 w-full lg:w-[420px]">
        <Card className="p-3 flex flex-col justify-center">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">Avg. Discharge Time</span>
            <Clock className="w-4 h-4 text-muted-foreground" />
          </div>
          {loading ? <Skeleton className="h-8 w-20" /> : (
            <span className="text-2xl font-bold text-foreground">
              {stats?.avgDischargeTime || "0h 0m"}
            </span>
          )}
        </Card>

        <Card className="p-3 flex flex-col justify-center">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">Total Escalations</span>
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />
          </div>
          {loading ? (
            <div className="flex gap-2">
              <Skeleton className="h-8 w-10" />
              <Skeleton className="h-8 w-10" />
              <Skeleton className="h-8 w-10" />
            </div>
          ) : (
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-2xl font-bold text-amber-500">
                {extended?.escalationsL1 || 0}
              </span>
              <span className="text-[10px] text-muted-foreground">L1</span>
              <span className="text-2xl font-bold text-red-400">
                {extended?.escalationsL2 || 0}
              </span>
              <span className="text-[10px] text-muted-foreground">L2</span>
              <span className="text-2xl font-bold text-red-700 dark:text-red-600">
                {extended?.escalationsL3 || 0}
              </span>
              <span className="text-[10px] text-muted-foreground">L3</span>
            </div>
          )}
        </Card>
      </div>

      {/* Right column bottom row: Patients Admitted + Payment Pending */}
      <div className="grid grid-cols-2 gap-3 w-full lg:w-[420px]">
        <Card className="p-3 flex flex-col justify-center">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">Patients Admitted</span>
            <UserCheck className="w-4 h-4 text-muted-foreground" />
          </div>
          {loading ? (
            <div className="flex gap-2">
              <Skeleton className="h-8 w-12" />
              <Skeleton className="h-7 w-10" />
            </div>
          ) : (
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-2xl font-bold text-foreground">
                {extended?.admittedToday || 0}
              </span>
              <span className="text-[10px] text-muted-foreground">Today</span>
              <span className="text-xl font-bold text-foreground ml-1">
                {extended?.admittedYesterday || 0}
              </span>
              <span className="text-[10px] text-muted-foreground">Yesterday</span>
            </div>
          )}
        </Card>

        <Card className="p-3 flex flex-col justify-center">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">Payment Pending</span>
            <Wallet className="w-4 h-4 text-muted-foreground" />
          </div>
          {loading ? <Skeleton className="h-8 w-12" /> : (
            <span className="text-2xl font-bold text-foreground">
              {stats?.paymentPending || 0}
            </span>
          )}
        </Card>
      </div>
    </div>
  );
};
